export class RequestTimeoutError extends Error {
  constructor() {
    super("The request took too long. Check your connection and try again.");
    this.name = "RequestTimeoutError";
  }
}

// The deadline encloses the whole operation, including reading its body. Abort
// stops the underlying fetch; the race also settles if a stalled transport or
// body reader does not respond to that abort. A caller's cancellation still wins.
export async function withRequestDeadline<T>(
  run: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  callerSignal?: AbortSignal | null,
): Promise<T> {
  const controller = new AbortController();
  let cancel: (reason: unknown) => void = () => {};
  const cancelled = new Promise<never>((_resolve, reject) => { cancel = reject; });
  const stop = (reason: unknown) => {
    cancel(reason);
    controller.abort(reason);
  };
  const onCallerAbort = () => stop(callerSignal?.reason ?? new DOMException("The request was cancelled.", "AbortError"));
  const timer = setTimeout(() => stop(new RequestTimeoutError()), timeoutMs);
  callerSignal?.addEventListener("abort", onCallerAbort, { once: true });
  if (callerSignal?.aborted) onCallerAbort();

  try {
    const operation = Promise.resolve().then(() => {
      controller.signal.throwIfAborted();
      return run(controller.signal);
    });
    return await Promise.race([operation, cancelled]);
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener("abort", onCallerAbort);
  }
}
